import React, { useContext, useMemo } from "react";
import {
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextareaAutosize,
  Icon,
} from "@mui/material";

import type { FormikConfig } from "formik";
import {
  Formik as FormikBase,
  Form as FormBase,
  Field,
  ErrorMessage,
} from "formik";
import * as Yup from "yup";
import { toastr } from "react-redux-toastr";

import { brandService } from "@/shared/services/brandService";
import { GlobalContext } from "@/helpers/contexts/contexts";
import RpMUIModal from "@/components/modals/RpMUIModal";
import { reportUserActivity } from "@/shared/helpers/userActivityHelper";
import { HTTP_STATUS } from "@/config/constants";
import RpSvgIcon from "@/components/icons/RpSvgIcon";
import { logger } from "@/helpers/loggerHelper";
import { shouldBlockUnsafeSubmission } from "@/helpers/sanitizeHelper";

type FeedbackProps = {
  handleClose: () => void;
  open: boolean;
};

type FeedbackValues = {
  feedback: string;
};

type FeedbackContext = {
  loggedInUser?: {
    id?: number | string;
    participantData?: {
      id?: number | string;
    } | null;
  } | null;
  selectedBrand?: {
    id?: number | string;
  } | null;
};

type FormikSubmitHelpers = {
  resetForm: () => void;
  setSubmitting: (_isSubmitting: boolean) => void;
};

type FeedbackResponse = {
  status?: number;
};

const Formik = FormikBase as unknown as React.ComponentType<
  FormikConfig<FeedbackValues>
>;
const Form = FormBase as unknown as React.ComponentType<
  React.FormHTMLAttributes<HTMLFormElement>
>;

const Feedback = ({ open, handleClose }: FeedbackProps) => {
  const { selectedBrand, loggedInUser } = useContext(
    GlobalContext
  ) as FeedbackContext;

  const userData = useMemo(
    () => ({
      userId: loggedInUser?.id,
      brandId: selectedBrand?.id,
      participantId: loggedInUser?.participantData?.id,
    }),
    [loggedInUser, selectedBrand]
  );

  const validationSchema = Yup.object({
    feedback: Yup.string()
      .required("Feedback is required")
      .test("not-only-spaces", "Please enter valid text.", (value) =>
        Boolean(value && value.trim().length > 0)
      )
      .max(1500, "Feedback cannot exceed 1500 characters"),
  });

  const initialValues = { feedback: "" };

  const handleSubmit = async (
    values: FeedbackValues,
    { resetForm, setSubmitting }: FormikSubmitHelpers
  ) => {
    if (shouldBlockUnsafeSubmission(values)) {
      setSubmitting(false);
      return;
    }

    reportUserActivity("pr_feedback");
    const feedbackData = {
      fk_user_id: userData.userId,
      fk_brand_id: userData.brandId,
      fk_participant_id: userData.participantId,
      feedback_message: values.feedback,
    };

    try {
      const response = (await brandService.saveUserFeedback(
        feedbackData
      )) as FeedbackResponse;

      if (response.status === HTTP_STATUS.OK) {
        toastr.success("Success", "Thank you for your feedback!");
        resetForm();
        handleClose();
      }
    } catch (error) {
      logger.error("Failed to submit feedback", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <RpMUIModal
      open={open}
      onClose={handleClose}
      heading="Feedback"
      maxWidth="sm"
      fullWidth
      className="user-feedback"
    >
      <div className="d-flex flex-column align-items-center">
        <div className="img-box text-center my-3">
          <Icon
            component={RpSvgIcon}
            name="comment"
            className="feedback-img"
            color="primary"
            size={150}
          />
        </div>
        <Typography variant="h5" component="p" className="fw-b text-center">
          We'd love your{" "}
          <Typography
            color="text.primary"
            variant="h5"
            component="span"
            className="fw-b ml-1 text-center"
          >
            Feedback!
          </Typography>
        </Typography>
        <Typography variant="subtitle2" component="p" className="text-center">
          What would you like to share?
        </Typography>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <DialogContent>
              <Field
                as={TextareaAutosize}
                minRows={3}
                maxRows={5}
                name="feedback"
                placeholder="Your message..."
                className="feedback-message p-2 w-100"
              />
              <ErrorMessage
                name="feedback"
                component="div"
                className="text-danger fs-12"
              />
            </DialogContent>

            <DialogActions className="d-flex justify-content-center my-3">
              <Button
                type="submit"
                className="w-auto feedback-btn"
                color="primary"
                variant="contained"
                size="small"
                disableElevation
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Send Feedback"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </RpMUIModal>
  );
};

export default Feedback;
